import React, { useMemo } from 'react'
import { useAtomValue } from 'jotai'
import { userAtom } from '../../store/userAtoms'
import { useTar } from '@/modules/i18n'
import { usePartnerDashboard } from '@/hooks/usePartnerDashboard'
import { useOrganization } from '@/hooks/useOrganization'
import { usePersons } from '@/hooks/usePersons'
import { useTaskDisplayNames } from '@/modules/entityLookups'
import { UserService } from '@duck/api-definitions'
import { Card, CardContent, Skeleton } from '@duck/ui'
import { NoDataEmptyState } from '../EmptyStates'
import { ClipboardDocumentListIcon } from '@heroicons/react/24/outline'
import { usePartnerTar } from '@/hooks/usePartnerTar'
import { PartnerDashboardTable } from './PartnerDashboardTable'
import { getCustomStatus, type PartnerDashboardRow } from '@/hooks/TableSettings/partnerDashboardTable.utils'

interface PartnerDashboardProps {
  organizationId?: string
}

export const PartnerDashboard: React.FC<PartnerDashboardProps> = ({ organizationId }) => {
  const user = useAtomValue(userAtom)
  const { t } = useTar()
  const { tP } = usePartnerTar()
  const { organization } = useOrganization(organizationId)
  const { recentAssignments, isLoading: isLoadingDashboard } =
    usePartnerDashboard(organizationId)

  const allPersonIds = useMemo(() => {
    return Array.from(new Set(recentAssignments.map(ad => ad.personId)))
  }, [recentAssignments])

  const { persons, isLoading: isLoadingPersons } = usePersons(allPersonIds)

  const taskIds = useMemo(() => {
    const ids = new Set<string>()
    recentAssignments.forEach((ad) => ids.add(ad.taskId))
    return Array.from(ids)
  }, [recentAssignments])

  const { getDisplayName, isLoading: isTaskLoading } = useTaskDisplayNames(taskIds, {
    fallback: t('common.na')
  })

  const flatRows = useMemo((): PartnerDashboardRow[] => {
    if (!recentAssignments || recentAssignments.length === 0) return []

    const personsMap = persons.reduce((acc, person) => {
      acc[person.id] = person
      return acc
    }, {} as Record<string, UserService.Person>)

    const rows: PartnerDashboardRow[] = []
    for (const ad of recentAssignments) {
      const person = personsMap[ad.personId]
      if (!person) continue

      rows.push({
        personId: ad.personId,
        assignmentId: ad.assignmentId,
        taskId: ad.taskId,
        taskDisplayName: getDisplayName(ad.taskId),
        email: person.email,
        onboardingStatus: person.onboardingStatus,
        assignmentStatus: ad.status,
        customStatus: getCustomStatus(person.onboardingStatus, ad.status),
        grade: ad.grade,
        feedback: ad.feedback,
        solution: ad.solution,
        startDate: ad.startDate,
        submitDate: ad.submitDate,
        workTime: ad.workTime,
        challengeId: ad.challengeId,
        charts: ad.charts,
        queries: ad.queries,
        assignmentSentDate: ad.createdAt,
        cohortName: ad.cohortName,
      })
    }
    return rows
  }, [recentAssignments, persons, getDisplayName])

  const isLoading = isLoadingDashboard || isLoadingPersons
  const hasNoAssignments = recentAssignments.length === 0 || flatRows.length === 0

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-foreground">
          {organizationId && organization?.name
            ? `${organization.name} – ${t('partner.dashboard')}`
            : t('partner.dashboard')}
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          {organizationId
            ? t('partner.dashboardDescriptionOrg')
            : t('partner.dashboardDescription', {
              firstName: user?.firstName || '',
              lastName: user?.lastName || ''
            })}
        </p>
      </div>

      {isLoading || isTaskLoading ? (
        <div className="space-y-6">
          <div className="overflow-x-auto">
            <div className="min-w-full border border-border rounded-md divide-y divide-border">
              <div className="bg-muted/15 px-6 py-3">
                <div className="flex items-center">
                  <div className="flex gap-6">
                    <Skeleton className="h-4 w-32" />
                    <Skeleton className="h-4 w-32" />
                    <Skeleton className="h-4 w-40" />
                    <Skeleton className="h-4 w-28" />
                    <Skeleton className="h-4 w-20" />
                  </div>
                  <div className="ml-auto flex items-center gap-2">
                    <Skeleton className="h-4 w-24" />
                    <Skeleton className="h-4 w-24" />
                  </div>
                </div>
              </div>

              <div className="bg-card divide-y divide-border">
                {Array.from({ length: 5 }).map((_, index) => (
                  <div
                    key={index}
                    className="flex items-center px-6 py-4 gap-6"
                  >
                    <div className="flex items-center gap-4 min-w-[220px]">
                      <div className="h-10 w-10 rounded-full bg-accent/10 flex items-center justify-center">
                        <Skeleton className="h-5 w-5 rounded-full" />
                      </div>
                      <div className="space-y-1">
                        <Skeleton className="h-4 w-32" />
                        <Skeleton className="h-3 w-24" />
                      </div>
                    </div>

                    <div className="flex-1 grid grid-cols-3 gap-6">
                      <Skeleton className="h-4 w-40" />
                      <Skeleton className="h-6 w-24 rounded-full" />
                      <Skeleton className="h-6 w-16 rounded-full" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      ) : hasNoAssignments ? (
        <Card>
          <CardContent className="pt-6">
            <NoDataEmptyState
              icon={ClipboardDocumentListIcon}
              title={tP('noAssignmentsYet')}
              description={tP('noAssignmentsDescription')}
            />
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          <PartnerDashboardTable rows={flatRows} persons={persons} />
        </div>
      )}
    </div>
  )
}
